import React from 'react';
import { ToastContainer } from 'react-toastify';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import BorrowerPage from './BorrowerPage';
import { ApplyStore, ApplyPersistor, UserStore } from '@apply/store';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import PropertyPage from './property/PropertyPage';
import FinalizePage from './finalize/FinalizePage';
import AppraisalPage from './appraisal/AppraisalPage';
import ReviewPage from './review/ReviewPage';
import { ThankYouPage } from './ThankYouPage';

import LoginPage, { useAuthentication } from './LoginPage';
import RegisterPage from './RegisterPage';
import ResetPasswordPage from './ResetPasswordPage';
import EditForm from './forms/EditForm';
import ForgotPasswordPage from './ForgotPasswordPage';
import HomePage from './home/HomePage';
import ResignAgreement from './home/ResignAgreement';
import NoticesPage from './home/NoticesPage';
import ExperiencePage from './home/ExperiencePage';
import { mainRoutes, ExpiredTokenModal } from '@apply/ui';
import { Payment } from './Payment';
import withAITracking from './ApplicationInsights';
import { ErrorBoundary } from './ErrorBoundary';
import DrawSummary from './draws/DrawSummary';
import BusinessAndPeoplePage from './home/BusinessAndPeoplePage';
import UmbrellaBusinessPage from './UmbrellaBusinessPage';

//v2
import { checkFeatureAndAuth } from './common/utilities/FeatureDetection';
import { ApplicationStyles } from './common/components/ApplicationStyles';
import { FeatureType } from '@apply/constants';
import Index from './home_v2/Index';

const {
    home,
    signAgreement,
    notices,
    experience,
    businessAndPeople,
    business,
    borrower,
    property,
    budget,
    landBudget,
    fixFlipBudget,
    specSheet,
    review,
    finalize,
    thankYou,
    appraisal,
} = mainRoutes; // broken out to create a back and next workflow.

const RequireAuth = ({ children }) => {
    return UserStore.Types.retrieve() ? children : <Navigate to={`/login?redirectTo=${location.pathname}`} />;
};

export const ApplyRouter: React.FC = () => {
    const oneHour = 3_600_000;
    const fiveMinutes = 300_000;

    const location = useLocation();
    const { logout } = useAuthentication();

    return (
        <>
            {UserStore.Types.retrieve() && (
                <ExpiredTokenModal
                    timeout={oneHour}
                    promptBeforeIdle={fiveMinutes}
                    onLogout={logout}
                />
            )}
            <Routes>
                {(location?.search.includes('print') || location?.pathname.includes('print')) && (
                    <>
                        <Route
                            path={`/${review}/:apiId/print`}
                            element={
                                <>
                                    <ApplicationStyles />
                                    <ReviewPage printMode={true} />
                                </>
                            }
                        />
                        <Route
                            path={`/${budget}/:apiId/print`}
                            element={
                                <>
                                    <ApplicationStyles />
                                    <EditForm
                                        formType='Budget'
                                        printMode={true}
                                    />
                                </>
                            }
                        />
                        <Route
                            path={`/${landBudget}/:apiId/print`}
                            element={
                                <>
                                    <ApplicationStyles />
                                    <EditForm
                                        formType='Land Budget'
                                        printMode={true}
                                    />
                                </>
                            }
                        />
                        <Route
                            path={`/${fixFlipBudget}/:apiId/print`}
                            element={
                                <>
                                    <ApplicationStyles />
                                    <EditForm
                                        formType='Fix-Flip'
                                        printMode={true}
                                    />
                                </>
                            }
                        />
                        <Route
                            path={`/${specSheet}/:apiId/print`}
                            element={
                                <>
                                    <ApplicationStyles />
                                    <EditForm
                                        formType='Spec Sheet'
                                        printMode={true}
                                    />
                                </>
                            }
                        />
                    </>
                )}

                <Route
                    path={`/${home}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles isFeatureDriven={true} />
                                {checkFeatureAndAuth(FeatureType.ApplyV2) ? <Index /> : <HomePage />}
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${notices}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <NoticesPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${experience}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <ExperiencePage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`${businessAndPeople}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <BusinessAndPeoplePage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${business}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <UmbrellaBusinessPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${signAgreement}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <ResignAgreement />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${borrower}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <BorrowerPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${property}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <PropertyPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${budget}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <EditForm formType='Budget' />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${landBudget}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <EditForm formType='Land Budget' />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${fixFlipBudget}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <EditForm formType='Fix-Flip' />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${specSheet}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <EditForm formType='Spec Sheet' />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${review}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <ReviewPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${thankYou}`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <ThankYouPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${finalize}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <FinalizePage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={`/${appraisal}/:apiId?`}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <AppraisalPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={'/draw/:drawBudgetId?'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles />
                                <DrawSummary />
                            </>
                        </RequireAuth>
                    }
                />

                <Route
                    path='/payment'
                    element={
                        <>
                            <ApplicationStyles />
                            <Payment />
                        </>
                    }
                />
                <Route
                    path='/login'
                    element={
                        <>
                            <ApplicationStyles />
                            <LoginPage />
                        </>
                    }
                />
                <Route
                    path='/register'
                    element={
                        <>
                            <ApplicationStyles />
                            <RegisterPage />
                        </>
                    }
                />
                <Route
                    path='/resetPassword'
                    element={
                        <>
                            <ApplicationStyles />
                            <ResetPasswordPage />
                        </>
                    }
                />
                <Route
                    path='/forgotPassword'
                    element={
                        <>
                            <ApplicationStyles />
                            <ForgotPasswordPage />
                        </>
                    }
                />

                <Route
                    path='/'
                    element={<Navigate to={`/${home}`} />}
                />
            </Routes>
        </>
    );
};

export const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <PersistGate
                persistor={ApplyPersistor}
                loading={null}
            >
                <Provider store={ApplyStore}>
                    <BrowserRouter>
                        <ApplyRouter />
                    </BrowserRouter>
                    <ToastContainer />
                </Provider>
            </PersistGate>
        </ErrorBoundary>
    );
};

export default withAITracking(App);
